import React, { useState } from 'react';
import {
  Image,
  ImageSourcePropType,
  ImageURISource,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface FilterChipProps extends PressableAnalyticsProps {
  /** Text of the component */
  labelText: string;
  /** SubText of the component */
  captionText?: string;
  /** Show image if true*/
  showImage?: boolean;
  /** Image of the component */
  imageSource?: ImageSourcePropType;
  /** Placeholder image source */
  placeHolderSource?: ImageSourcePropType;
  /** Default image source */
  defaultSource?: ImageURISource;
  /** Color indicator of the component */
  colorIndicator?: string;
  /** Component is disabled */
  isDisabled: boolean;
  /** Component is selected */
  isSelected: boolean;
  /** Function to call on press */
  onPress?: () => void;
}

interface ComposableStyle {
  containerStyle: ViewStyle;
}

const composeComponentStyles = (
  isHovered,
  isPressed,
  isFocused,
  isDisabled,
  isSelected,
): ComposableStyle => {
  if (isDisabled) {
    return {
      containerStyle: isSelected
        ? baseStyles.containerDisabledSelected
        : baseStyles.containerDisabledUnselected,
    };
  } else if (isHovered) {
    return {
      containerStyle: baseStyles.containerHovered,
    };
  } else if (isFocused) {
    return {
      containerStyle: baseStyles.containerFocused,
    };
  } else if (isPressed) {
    return {
      containerStyle: baseStyles.containerPressed,
    };
  } else if (isSelected) {
    return {
      containerStyle: baseStyles.containerSelected,
    };
  }
  return {
    containerStyle: baseStyles.containerUnselected,
  };
};

export const FilterChip = ({
  labelText,
  captionText,
  showImage = true,
  imageSource,
  colorIndicator,
  isDisabled = false,
  isSelected = false,
  placeHolderSource,
  onPress,
  analyticsId = '',
  customAttributes = null,
}: FilterChipProps) => {
  const analytics = usePressableAnalytics({ analyticsId, customAttributes });
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [image, setImageSource] = useState(imageSource);

  const computedStyles = composeComponentStyles(
    isPressed,
    isFocused,
    isHovered,
    isDisabled,
    isSelected,
  );

  const textColor = isDisabled
    ? baseStyles.mutedStyle
    : baseStyles.primaryStyle;

  const handleError = () => {
    setImageSource(placeHolderSource); // Placeholder image
  };

  return (
    <PressableWithDelay
      onPress={() => {
        if (onPress) {
          analytics();
          onPress();
        }
      }}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      style={[baseStyles.containerWrapper, computedStyles.containerStyle]}
      role="listitem"
    >
      {showImage && imageSource ? (
        <Image
          onError={handleError}
          source={image}
          style={baseStyles.imageStyle}
        />
      ) : null}

      {colorIndicator && !imageSource ? (
        <View
          style={[
            baseStyles.colorIndicatorStyle,
            { backgroundColor: colorIndicator },
          ]}
        />
      ) : null}
      <View style={baseStyles.textWrapper}>
        <BodySmBoldText
          style={[!captionText && baseStyles.centerText, textColor]}
          numberOfLines={1}
          ellipsizeMode="tail"
          id={labelText}
        >
          {labelText}
        </BodySmBoldText>
        {captionText && (
          <BodyXSText
            style={textColor}
            id={captionText}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {captionText}
          </BodyXSText>
        )}
      </View>
    </PressableWithDelay>
  );
};

const baseStyles = StyleSheet.create({
  centerText: {
    // center the title when subText is null
    justifyContent: 'center',
    textAlign: 'center',
  },
  colorIndicatorStyle: {
    borderRadius: 4,
    height: 16,
    width: 16,
  },
  containerDisabledSelected: {
    borderColor: theme.color.interactive.disabled.border,
  },
  containerDisabledUnselected: {
    borderColor: theme.color.border.primarySolid,
  },
  containerFocused: {
    borderColor: theme.color.border.focused,
  },
  containerHovered: {
    borderColor: theme.color.interactive.blackHovered,
  },
  containerPressed: {
    borderColor: theme.color.interactive.blackPressed,
  },
  containerSelected: {
    borderColor: theme.color.interactive.black,
  },
  containerUnselected: {
    borderColor: theme.color.border.primarySolid,
  },
  containerWrapper: {
    alignContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    borderColor: theme.color.border.primarySolid,
    borderRadius: 4,
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.s,
    height: 44,
    justifyContent: 'center',
    maxWidth: 232,
    paddingHorizontal: 12,
    paddingVertical: theme.spacing.xs,
    textAlign: 'left',
  },
  imageStyle: {
    height: 32,
    width: 32,
  },
  mutedStyle: {
    color: theme.color.interactive.disabled.text,
  },
  primaryStyle: {
    color: theme.color.text.primary,
  },
  textWrapper: {
    alignItems: 'flex-start',
    display: 'flex',
    flexBasis: 'auto',
    flexDirection: 'column',
    flexShrink: 1,
    justifyContent: 'center',
  },
});

export default FilterChip;
